import { Controller } from "@hotwired/stimulus"
import { captureBackdropDrops, carriesFiles, flashFileWarning, partitionByAccept } from "../../utils/file-upload.util"

// Fallbacks for the server-side copy passed in through the `translations` value.
const DEFAULT_TRANSLATIONS = {
  unsupportedFileText: "Unsupported file type and was skipped: ",
  tooManyFilesText: "Too many files selected.",
  removeFileText: "Remove file"
}

// Connects to data-controller="finance-management--submission-upload"
// The two FS submission offcanvases: the accountant's XML forms (several at
// once, up to maxFiles) and the signed .asice (maxFiles = 1). Mirrors
// documents--document-upload — a plain multipart POST, this controller only
// owns the picker, drag & drop, the chosen-file list and the submit button —
// and resets itself whenever the offcanvas is hidden.
export default class extends Controller {
  static targets = ["fileInput", "submitButton", "placeholder", "list", "items", "count"]

  static values = {
    translations: { type: Object, default: {} },
    maxFiles: { type: Number, default: 20 }
  }

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  connect() {
    this.files = []
    this.resetHandler = () => this.reset()
    this.element.addEventListener("hidden.bs.offcanvas", this.resetHandler)

    this._releaseBackdropDrops = captureBackdropDrops({
      element: this.element,
      enabled: () => this.hasFileInputTarget,
      onFiles: (files) => this.takeFiles(files),
      setDragging: (active) => this.#setDragging(active)
    })
  }

  disconnect() {
    this._releaseBackdropDrops?.()
    this.element.removeEventListener("hidden.bs.offcanvas", this.resetHandler)
  }

  dragOver(event) {
    if (!this.#acceptsDrop(event)) return

    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy"
    this.#setDragging(true)
  }

  dragLeave(event) {
    if (event.relatedTarget && this.element.contains(event.relatedTarget)) return

    this.#setDragging(false)
  }

  // Bound to the whole offcanvas so a drop that misses the artwork can't fall
  // through to the browser default and navigate the tab to the dropped file.
  drop(event) {
    if (!this.#acceptsDrop(event)) return

    event.preventDefault()
    this.#setDragging(false)
    this.takeFiles(Array.from(event.dataTransfer.files))
  }

  // The picker path: what it hands us still has to clear `accept`.
  fileChanged() {
    this.takeFiles(Array.from(this.fileInputTarget.files))
  }

  // A single-file panel keeps the last file; a multi-file panel appends up to maxFiles.
  takeFiles(incoming) {
    if (!this.hasFileInputTarget) return

    const accepted = this.#filterAccepted(incoming)
    if (accepted.length === 0) {
      this.#sync()
      return
    }

    if (this.maxFilesValue === 1) {
      if (accepted.length > 1) flashFileWarning(this.#texts.tooManyFilesText)
      this.files = [accepted[0]]
    } else {
      const room = Math.max(this.maxFilesValue - this.files.length, 0)
      if (accepted.length > room) flashFileWarning(this.#texts.tooManyFilesText)
      this.files = [...this.files, ...accepted.slice(0, room)]
    }
    this.#sync()
  }

  removeFile(event) {
    this.files.splice(Number(event.params.index), 1)
    this.#sync()
  }

  clearFiles() {
    this.files = []
    this.#sync()
  }

  reset() {
    this.#setDragging(false)
    if (!this.hasFileInputTarget) return

    this.files = []
    this.#sync()
  }

  #acceptsDrop(event) {
    return this.hasFileInputTarget && carriesFiles(event)
  }

  #setDragging(active) {
    this.element.classList.toggle("file-drop--active", active)
  }

  #filterAccepted(incoming) {
    const { accepted, rejected } = partitionByAccept(incoming, this.fileInputTarget.accept)
    for (const file of rejected) {
      flashFileWarning(`${this.#texts.unsupportedFileText}${file.name}`)
    }
    return accepted
  }

  // The input's FileList is rebuilt from `this.files` so the plain form POST
  // sends exactly what the list shows.
  #sync() {
    const transfer = new DataTransfer()
    for (const file of this.files) transfer.items.add(file)
    this.fileInputTarget.files = transfer.files

    this.itemsTarget.replaceChildren(...this.files.map((file, index) => this.#item(file, index)))
    if (this.hasCountTarget) this.countTarget.textContent = String(this.files.length)

    const any = this.files.length > 0
    this.listTarget.classList.toggle("d-none", !any)
    this.placeholderTarget.classList.toggle("d-none", any && this.maxFilesValue === 1)
    this.submitButtonTarget.disabled = !any
  }

  // One row of the chosen-file list (the expenses preview markup), built with
  // textContent — file names are user input.
  #item(file, index) {
    const item = document.createElement("div")
    item.className = "attachments__item attachments__item--preview"

    const icon = document.createElement("div")
    icon.className = "attachments__icon"
    const glyph = document.createElement("span")
    glyph.className = "material-symbols-outlined"
    glyph.textContent = "attach_file"
    icon.append(glyph)

    const content = document.createElement("div")
    content.className = "attachments__content"
    const details = document.createElement("div")
    details.className = "attachments__details"
    const name = document.createElement("div")
    name.className = "attachments__name"
    name.textContent = file.name
    const size = document.createElement("div")
    size.className = "attachments__description"
    size.textContent = this.formatBytes(file.size)
    details.append(name, size)

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "attachments__remove"
    // Named with the file so a screen reader can tell the rows' buttons apart.
    remove.setAttribute("aria-label", `${this.#texts.removeFileText}: ${file.name}`)
    remove.dataset.action = `click->${this.identifier}#removeFile`
    // Stimulus reads action params from data-<identifier>-<name>-param; the
    // identifier's double dash cannot be expressed through `dataset`.
    remove.setAttribute(`data-${this.identifier}-index-param`, String(index))
    const cross = document.createElement("span")
    cross.className = "material-symbols-outlined"
    cross.textContent = "close"
    remove.append(cross)

    content.append(details, remove)
    item.append(icon, content)
    return item
  }

  formatBytes(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
}
